import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { PartyEntityCodes } from '../types/PartyEntityCodes';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import RecordService from '@/services/RecordService/RecordService';
import { PARTY_DETAILS_ENDPOINT_MAP } from '../constants/PartyDetailsEndpointMap';
import {
  getPartyDetailsBeneficiaryOnListData,
  getPartyDetailsBeneficiaryOnListDataError,
  getPartyDetailsBeneficiaryOnListDataSuccess,
  getPartyDetailsClaimantOnListData,
  getPartyDetailsClaimantOnListDataError,
  getPartyDetailsClaimantOnListDataSuccess,
  getPartyDetailsHasContactsOfListData,
  getPartyDetailsHasContactsOfListDataError,
  getPartyDetailsHasContactsOfListDataSuccess,
  getPartyDetailsInsuredOnListData,
  getPartyDetailsInsuredOnListDataError,
  getPartyDetailsInsuredOnListDataSuccess,
  getPartyDetailsIsContactOfListData,
  getPartyDetailsIsContactOfListDataError,
  getPartyDetailsIsContactOfListDataSuccess,
  incrementPartyDetailsBeneficiaryOnListPage,
  incrementPartyDetailsClaimantOnListPage,
  incrementPartyDetailsHasContactsOfListPage,
  incrementPartyDetailsInsuredOnListPage,
  incrementPartyDetailsIsContactOfListPage,
  startLoadingPartyDetailsBeneficiaryOnListData,
  startLoadingPartyDetailsClaimantOnListData,
  startLoadingPartyDetailsHasContactsOfListData,
  startLoadingPartyDetailsInsuredOnListData,
} from '../store/partyDetailsTabList.slice';
import {
  selectPartyDetailsBeneficiaryOnListPagination,
  selectPartyDetailsClaimantOnListPagination,
  selectPartyDetailsHasContactOfListPagination,
  selectPartyDetailsInsuredOnListPagination,
  selectPartyDetailsIsContactOfListPagination,
} from '../store/selectors/partyDetailsTabListSelectors';

function* fetchPartyDetailsRelatedIds(params: {
  partyId: string;
  entityType: PartyEntityCodes;
}): Generator<any, string[], any> {
  const { partyId, entityType } = params;

  const endpoint = PARTY_DETAILS_ENDPOINT_MAP[entityType];

  if (!endpoint) {
    throw new Error(`No endpoint mapping found for entityType: ${entityType}`);
  }

  const { data }: { data: Array<{ value: string }> } = yield call(
    HttpService.get,
    `/parties/PartyPanel/${partyId}/${endpoint}`,
  );

  const responseIds: string[] = (data || [])
    .map((item) => item?.value)
    .filter((value): value is string => Boolean(value));

  if (!responseIds.length) {
    throw new Error(`No responseIds returned from ${endpoint}`);
  }

  return responseIds;
}

function* getPartyDetailsInsuredOnListDataSaga({ type, payload }: PayloadAction<{ id: string }>) {
  try {
    yield put(startLoadingPartyDetailsInsuredOnListData());

    const pagination = yield select(selectPartyDetailsInsuredOnListPagination);

    const response: string = yield call(fetchPartyDetailsRelatedIds, {
      partyId: payload.id,
      entityType: PartyEntityCodes.Insured,
    });

    const filters = [{ propertyName: 'Id', values: response }];

    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags],
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters, {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => ({
      ...normalizedClaim,
      entities: {
        ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
      },
    }));

    yield put(
      getPartyDetailsInsuredOnListDataSuccess({
        shouldRewrite: type !== incrementPartyDetailsInsuredOnListPage.type,
        data: {
          listings: claimsWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    yield put(getPartyDetailsInsuredOnListDataError());
    console.error(error);
  }
}

function* getPartyDetailsClaimantOnListDataSaga({ type, payload }: PayloadAction<{ id: string }>) {
  try {
    yield put(startLoadingPartyDetailsClaimantOnListData());

    const pagination = yield select(selectPartyDetailsClaimantOnListPagination);

    const response: string = yield call(fetchPartyDetailsRelatedIds, {
      partyId: payload.id,
      entityType: PartyEntityCodes.Claimant,
    });

    const filters = [{ propertyName: 'Id', values: response }];
    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags] })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => {
      return {
        ...normalizedClaim,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
        },
      };
    });

    yield put(
      getPartyDetailsClaimantOnListDataSuccess({
        shouldRewrite: type !== incrementPartyDetailsClaimantOnListPage.type,
        data: {
          listings: claimsWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    yield put(getPartyDetailsClaimantOnListDataError());
    console.error(error);
  }
}

function* getPartyDetailsBeneficiaryOnListDataSaga({ type, payload }: PayloadAction<{ id: string }>) {
  try {
    yield put(startLoadingPartyDetailsBeneficiaryOnListData());

    const pagination = yield select(selectPartyDetailsBeneficiaryOnListPagination);

    const response: string = yield call(fetchPartyDetailsRelatedIds, {
      partyId: payload.id,
      entityType: PartyEntityCodes.Beneficiary,
    });

    const filters = [{ propertyName: 'Id', values: response }];
    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags] })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => {
      return {
        ...normalizedClaim,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
        },
      };
    });

    yield put(
      getPartyDetailsBeneficiaryOnListDataSuccess({
        shouldRewrite: type !== incrementPartyDetailsBeneficiaryOnListPage.type,
        data: {
          listings: claimsWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    yield put(getPartyDetailsBeneficiaryOnListDataError());
    console.error(error);
  }
}

function* getPartyDetailsIsContactOfListDataSaga({ type, payload }: PayloadAction<{ id: string }>) {
  try {
    yield put(startLoadingPartyDetailsInsuredOnListData());

    const pagination = yield select(selectPartyDetailsIsContactOfListPagination);

    const response: string = yield call(fetchPartyDetailsRelatedIds, {
      partyId: payload.id,
      entityType: PartyEntityCodes.IsContact,
    });

    const filters = [{ propertyName: 'Id', values: response }];
    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags] })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => {
      return {
        ...normalizedClaim,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
        },
      };
    });

    yield put(
      getPartyDetailsIsContactOfListDataSuccess({
        shouldRewrite: type !== incrementPartyDetailsIsContactOfListPage.type,
        data: {
          listings: claimsWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    yield put(getPartyDetailsIsContactOfListDataError());
    console.error(error);
  }
}

function* getPartyDetailsHasContactsOfListDataSaga({ type, payload }: PayloadAction<{ id: string }>) {
  try {
    yield put(startLoadingPartyDetailsHasContactsOfListData());

    const pagination = yield select(selectPartyDetailsHasContactOfListPagination);

    const response: string = yield call(fetchPartyDetailsRelatedIds, {
      partyId: payload.id,
      entityType: PartyEntityCodes.HasContacts,
    });

    const filters = [{ propertyName: 'Id', values: response }];
    const requestUrl = `/filter/${EdsEntityCodes.ClaimEntity}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, includes: [EdsEntityCodes.Milestone, EdsEntityCodes.HelperTags] })}`;

    const { data } = yield call(HttpService.post, requestUrl, filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    const claimsWithoutAvailableHelperTags = normalizedResponse.map((normalizedClaim) => {
      return {
        ...normalizedClaim,
        entities: {
          ...RecordService.omit([EdsEntityCodes.AvailableHelperTags], normalizedClaim.entities),
        },
      };
    });

    yield put(
      getPartyDetailsHasContactsOfListDataSuccess({
        shouldRewrite: type !== incrementPartyDetailsHasContactsOfListPage.type,
        data: {
          listings: claimsWithoutAvailableHelperTags,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    yield put(getPartyDetailsHasContactsOfListDataError());
    console.error(error);
  }
}

export function* partyDetailsTabsWatcher() {
  yield takeLatest(
    [getPartyDetailsInsuredOnListData.type, incrementPartyDetailsInsuredOnListPage.type],
    getPartyDetailsInsuredOnListDataSaga,
  );
  yield takeLatest(
    [getPartyDetailsClaimantOnListData.type, incrementPartyDetailsClaimantOnListPage.type],
    getPartyDetailsClaimantOnListDataSaga,
  );
  yield takeLatest(
    [getPartyDetailsBeneficiaryOnListData.type, incrementPartyDetailsBeneficiaryOnListPage.type],
    getPartyDetailsBeneficiaryOnListDataSaga,
  );
  yield takeLatest(
    [getPartyDetailsIsContactOfListData.type, incrementPartyDetailsIsContactOfListPage.type],
    getPartyDetailsIsContactOfListDataSaga,
  );
  yield takeLatest(
    [getPartyDetailsHasContactsOfListData.type, incrementPartyDetailsHasContactsOfListPage.type],
    getPartyDetailsHasContactsOfListDataSaga,
  );
}
